export const CONTINUATION_PROTOCOL = 'continuation_v1' as const

export type ContinuationProtocol = typeof CONTINUATION_PROTOCOL

export interface ContinuationToolCall {
  id: string
  type: 'function'
  function: {
    name: string
    arguments: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

export interface ContinuationAssistantMessage {
  role: 'assistant'
  content: string | null
  tool_calls?: ContinuationToolCall[]
  reasoning_content?: string
  reasoning?: string
  reasoning_details?: Record<string, unknown>[]
  refusal?: string | null
  [key: string]: unknown
}

export interface ContinuationToolResultMessage {
  role: 'tool'
  content: string
  tool_call_id: string
  [key: string]: unknown
}

export interface ContinuationDoneEnvelope {
  stream_id: string
  tool_protocol: ContinuationProtocol
  status: 'completed' | 'tool_calls'
  assistant_message: ContinuationAssistantMessage
  finish_reason: 'stop' | 'tool_calls'
  usage: Record<string, unknown> | null
  model: string | null
  provider_model: string | null
  [key: string]: unknown
}

export interface ContinuationErrorMetadata {
  streamId?: string
  finishReason?: string | null
  usage?: Record<string, unknown> | null
  partialAssistantMessage?: Record<string, unknown>
}

export class ContinuationStreamError extends Error {
  readonly streamId?: string
  readonly finishReason?: string | null
  readonly usage?: Record<string, unknown> | null
  readonly partialAssistantMessage?: Record<string, unknown>
  status?: number

  constructor(message: string, metadata: ContinuationErrorMetadata = {}) {
    super(message)
    this.name = 'ContinuationStreamError'
    this.streamId = metadata.streamId
    this.finishReason = metadata.finishReason
    this.usage = metadata.usage
    this.partialAssistantMessage = metadata.partialAssistantMessage
  }
}

export class ContinuationProtocolError extends ContinuationStreamError {
  constructor(message: string, metadata: ContinuationErrorMetadata = {}) {
    super(message, metadata)
    this.name = 'ContinuationProtocolError'
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string'

function requireNullableString(
  value: unknown,
  field: string,
): asserts value is string | null {
  if (!isNullableString(value)) {
    throw new ContinuationProtocolError(`[RelayAdapter] Invalid continuation ${field}.`)
  }
}

const validateToolCalls = (value: unknown): ContinuationToolCall[] => {
  if (!Array.isArray(value)) {
    throw new ContinuationProtocolError('[RelayAdapter] Invalid continuation tool_calls.')
  }

  const ids = new Set<string>()
  for (const item of value) {
    if (!isRecord(item) || typeof item.id !== 'string' || item.id.length === 0) {
      throw new ContinuationProtocolError('[RelayAdapter] Continuation tool calls require nonempty IDs.')
    }
    if (ids.has(item.id)) {
      throw new ContinuationProtocolError('[RelayAdapter] Continuation tool call IDs must be unique.')
    }
    ids.add(item.id)

    if (item.type !== 'function' || !isRecord(item.function)) {
      throw new ContinuationProtocolError('[RelayAdapter] Invalid continuation tool call type.')
    }
    if (typeof item.function.name !== 'string' || item.function.name.length === 0) {
      throw new ContinuationProtocolError('[RelayAdapter] Continuation tool calls require function names.')
    }
    if (typeof item.function.arguments !== 'string') {
      throw new ContinuationProtocolError('[RelayAdapter] Continuation tool arguments must be raw strings.')
    }
    try {
      JSON.parse(item.function.arguments)
    } catch {
      throw new ContinuationProtocolError('[RelayAdapter] Continuation tool arguments must contain valid JSON.')
    }
  }

  return value as ContinuationToolCall[]
}

/**
 * Validate the relay's authoritative terminal payload without rebuilding it.
 * Unknown properties remain on the returned object for lossless replay.
 */
export function validateContinuationDoneEnvelope(
  value: unknown,
  expectedStreamId: string,
): ContinuationDoneEnvelope {
  if (!isRecord(value)) {
    throw new ContinuationProtocolError('[RelayAdapter] Continuation done payload must be an object.')
  }
  if (value.tool_protocol !== CONTINUATION_PROTOCOL) {
    throw new ContinuationProtocolError('[RelayAdapter] Continuation done payload has the wrong protocol.')
  }
  if (typeof value.stream_id !== 'string' || value.stream_id.length === 0 || value.stream_id !== expectedStreamId) {
    throw new ContinuationProtocolError('[RelayAdapter] Continuation done payload has the wrong stream identity.')
  }
  if (value.status !== 'completed' && value.status !== 'tool_calls') {
    throw new ContinuationProtocolError('[RelayAdapter] Invalid continuation status.')
  }
  if (!isRecord(value.assistant_message) || value.assistant_message.role !== 'assistant') {
    throw new ContinuationProtocolError('[RelayAdapter] Invalid continuation assistant message.')
  }

  const assistant = value.assistant_message
  requireNullableString(assistant.content, 'assistant content')
  if (assistant.reasoning_content !== undefined && typeof assistant.reasoning_content !== 'string') {
    throw new ContinuationProtocolError('[RelayAdapter] Invalid continuation reasoning_content.')
  }
  if (assistant.reasoning !== undefined && typeof assistant.reasoning !== 'string') {
    throw new ContinuationProtocolError('[RelayAdapter] Invalid continuation reasoning.')
  }
  if (
    assistant.reasoning_details !== undefined
    && (!Array.isArray(assistant.reasoning_details) || assistant.reasoning_details.some((detail) => !isRecord(detail)))
  ) {
    throw new ContinuationProtocolError('[RelayAdapter] Invalid continuation reasoning_details.')
  }
  if (assistant.refusal !== undefined && !isNullableString(assistant.refusal)) {
    throw new ContinuationProtocolError('[RelayAdapter] Invalid continuation refusal.')
  }
  if (typeof assistant.refusal === 'string' && assistant.refusal.length > 0) {
    throw new ContinuationProtocolError('[RelayAdapter] Provider refused the continuation request.')
  }

  const toolCalls = assistant.tool_calls === undefined ? [] : validateToolCalls(assistant.tool_calls)
  requireNullableString(value.model, 'model')
  requireNullableString(value.provider_model, 'provider_model')
  if (value.usage !== null && !isRecord(value.usage)) {
    throw new ContinuationProtocolError('[RelayAdapter] Invalid continuation usage.')
  }

  if (value.status === 'tool_calls') {
    if (value.finish_reason !== 'tool_calls' || toolCalls.length === 0) {
      throw new ContinuationProtocolError('[RelayAdapter] Inconsistent continuation tool-call status.')
    }
  } else if (
    value.finish_reason !== 'stop'
    || toolCalls.length > 0
  ) {
    throw new ContinuationProtocolError('[RelayAdapter] Inconsistent completed continuation status.')
  }

  return value as unknown as ContinuationDoneEnvelope
}

/** Keep only documented relay diagnostics; never turn a partial message into success. */
export function continuationErrorFromPayload(value: unknown): ContinuationStreamError {
  if (!isRecord(value)) {
    return new ContinuationStreamError('[RelayAdapter] Unknown continuation stream error')
  }

  const message = typeof value.error === 'string' && value.error.length > 0
    ? value.error
    : '[RelayAdapter] Unknown continuation stream error'
  const metadata: ContinuationErrorMetadata = {}
  if (typeof value.stream_id === 'string') metadata.streamId = value.stream_id
  if (value.finish_reason === null || typeof value.finish_reason === 'string') {
    metadata.finishReason = value.finish_reason
  }
  if (value.usage === null || isRecord(value.usage)) metadata.usage = value.usage
  if (isRecord(value.partial_assistant_message)) {
    metadata.partialAssistantMessage = value.partial_assistant_message
  }
  return new ContinuationStreamError(message, metadata)
}
